// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { API_VERSION } from '@/constants/api-version';
import { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

const DEPLOYMENT_HEADER = 'x-deployment-id';
const API_VERSION_HEADER = 'x-api-version';

/**
 * Returns the deployment ID from the response headers. If the header is not
 * present, this function returns null.
 */
function getDeploymentId(response: AxiosResponse): string | null {
  const deploymentId: unknown = response.headers[DEPLOYMENT_HEADER];

  if (typeof deploymentId === 'string') {
    return deploymentId;
  }

  return null;
}

/**
 * Returns the API version from the response headers. If the header is not
 * present, this function returns null.
 */
function getApiVersionFromHeader(response: AxiosResponse): string | null {
  const apiVersion: unknown = response.headers[API_VERSION_HEADER];

  if (typeof apiVersion === 'string') {
    return apiVersion;
  }

  return null;
}

/**
 * Returns the app version from the environment variables (deployment ID).
 * If the version is not present, this function returns null.
 */
function getAppVersion(): string | null {
  const version: unknown = import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA;

  if (isEmpty(version)) {
    return null;
  }

  if (typeof version === 'string') {
    return version;
  }

  return null;
}

/**
 * Parses an API version string into its date and revision components.
 * Supports formats: "YYYY-MM-DD" or "YYYY-MM-DD-R<number>"
 */
export function parseApiVersion(version: string): {
  date: string;
  revision: number;
} {
  const match = version.match(/^(\d{4}-\d{2}-\d{2})(?:-R(\d+))?$/);
  if (!match) {
    return { date: version, revision: 0 };
  }
  return {
    date: match[1],
    revision: match[2] ? parseInt(match[2], 10) : 0,
  };
}

/**
 * Compares two API versions to determine if the current version is compatible
 * with (greater than or equal to) the expected version.
 */
export function isApiVersionCompatible(
  currentVersion: string,
  expectedVersion: string
): boolean {
  const current = parseApiVersion(currentVersion);
  const expected = parseApiVersion(expectedVersion);

  if (current.date > expected.date) return true;
  if (current.date < expected.date) return false;
  return current.revision >= expected.revision;
}

/** Checks whether the deployment ID matches the running bundle. */
function isBundleLatest(deploymentId: string | null): boolean {
  const appVersion = getAppVersion();

  if (deploymentId === null || appVersion === null) {
    return true;
  }

  return appVersion === deploymentId;
}

/** Checks whether the server's API version is newer than this bundle's. */
function hasNewerApiContract(serverApiVersion: string | null): boolean {
  if (serverApiVersion === null) {
    return false;
  }

  return !isApiVersionCompatible(API_VERSION, serverApiVersion);
}

/** Checks whether a newer bundle is deployed for a newer API contract. */
function isBundleIncompatible(
  deploymentId: string | null,
  serverApiVersion: string | null
): boolean {
  // A reload only helps once Vercel serves a bundle built for the newer contract.
  return !isBundleLatest(deploymentId) && hasNewerApiContract(serverApiVersion);
}

// The latest response's version headers, kept outside React for non-React callers
let latestDeploymentId: string | null = null;
let latestServerApiVersion: string | null = null;

/** Checks whether the latest response left this bundle awaiting its forced reload. */
export function isBundleReloadPending(): boolean {
  return isBundleIncompatible(latestDeploymentId, latestServerApiVersion);
}

interface AppVersionData {
  /** Whether the deployed frontend is the latest version (for blue info snackbar) */
  isLatest: boolean;
  /** The deployment ID from the response header */
  version: string | null;
  /** Whether this bundle still matches the server's API contract (for yellow warning) */
  isApiCompatible: boolean;
  /** The current API version from response headers */
  currentApiVersion: string | null;
  /** Function to check API version (useful for visibility change) */
  checkApiVersion: () => Promise<boolean>;
}

/**
 * A React hook that tracks both deployment version and API version.
 *
 * - Deployment version (isLatest): Compares the frontend's build version against
 *   what the backend reports, to detect when a new frontend has been deployed.
 *   Shows as blue info snackbar.
 *
 * - API version (isApiCompatible): Compares the API version this bundle was
 *   built against with subsequent responses, to detect breaking API changes.
 *   Shows as yellow warning with auto-reload behavior.
 */
export function useAppVersion(): AppVersionData {
  const [version, setVersion] = useState<string | null>(null);
  const [currentApiVersion, setCurrentApiVersion] = useState<string | null>(
    null
  );

  // Set up response interceptor to track versions from headers
  useEffect(() => {
    const interceptor = api.interceptors.response.use((response) => {
      const deploymentId = getDeploymentId(response);
      latestDeploymentId = deploymentId;
      setVersion(deploymentId);
      const apiVersion = getApiVersionFromHeader(response);
      if (apiVersion !== null) {
        latestServerApiVersion = apiVersion;
        setCurrentApiVersion(apiVersion);
      }

      return response;
    });

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  const isLatest = isBundleLatest(version);
  const isApiCompatible = !isBundleIncompatible(version, currentApiVersion);

  /** Checks /meta/version for an incompatible bundle, such as on tab focus. */
  const checkApiVersion = useCallback(async (): Promise<boolean> => {
    try {
      const response = await api.get<{ data: { version: string } }>(
        '/meta/version'
      );
      return isBundleIncompatible(
        getDeploymentId(response),
        response.data.data.version
      );
    } catch {
      return false;
    }
  }, []);

  return {
    isLatest,
    version,
    isApiCompatible,
    currentApiVersion,
    checkApiVersion,
  };
}
